import { canv } from "@/canv/global";
import { MindMapWidget } from "@/elements/mindMap";
import { eventEmitter } from "@/eventEmitter";
import { Group } from "@/group";
import { t } from "@/i18n";
import { MIDNMAP_LIST_DATA } from "@/interface/SideBar/MindMapMenu/constants";
import { toolBase } from "@/tools/toolBase";

type Direction = "direct" | "reverse";

export class MindMapTool extends toolBase {
    getToolView() {
        return `
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <mask x="0" y="0" width="24" height="24">
                    <path d="M24 24V0H0V24H24Z" fill="white"/>
                </mask>
                <path d="M10 4C10 4.53043 10.2107 5.03914 10.5858 5.41421C10.9609 5.78929 11.4696 6 12 6C12.5304 6 13.0391 5.78929 13.4142 5.41421C13.7893 5.03914 14 4.53043 14 4C14 3.46957 13.7893 2.96086 13.4142 2.58579C13.0391 2.21071 12.5304 2 12 2C11.4696 2 10.9609 2.21071 10.5858 2.58579C10.2107 2.96086 10 3.46957 10 4ZM20 21C20 21.2652 20.1054 21.5196 20.2929 21.7071C20.4804 21.8946 20.7348 22 21 22C21.2652 22 21.5196 21.8946 21.7071 21.7071C21.8946 21.5196 22 21.2652 22 21C22 20.7348 21.8946 20.4804 21.7071 20.2929C21.5196 20.1054 21.2652 20 21 20C20.7348 20 20.4804 20.1054 20.2929 20.2929C20.1054 20.4804 20 20.7348 20 21ZM11 21C11 21.2652 11.1054 21.5196 11.2929 21.7071C11.4804 21.8946 11.7348 22 12 22C12.2652 22 12.5196 21.8946 12.7071 21.7071C12.8946 21.5196 13 21.2652 13 21C13 20.7348 12.8946 20.4804 12.7071 20.2929C12.5196 20.1054 12.2652 20 12 20C11.7348 20 11.4804 20.1054 11.2929 20.2929C11.1054 20.4804 11 20.7348 11 21ZM2 21C2 21.2652 2.10536 21.5196 2.29289 21.7071C2.48043 21.8946 2.73478 22 3 22C3.26522 22 3.51957 21.8946 3.70711 21.7071C3.89464 21.5196 4 21.2652 4 21C4 20.7348 3.89464 20.4804 3.70711 20.2929C3.51957 20.1054 3.26522 20 3 20C2.73478 20 2.48043 20.1054 2.29289 20.2929C2.10536 20.4804 2 20.7348 2 21Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
                <path d="M21 16V10H3V16M12 6V16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <defs>
                    <rect width="24" height="24" fill="white"/>
                    <rect width="24" height="24" fill="white"/>
                </defs>
            </svg>
        `;
    }

    getToolTipName() {
        return t("tools.mindMap");
    }

    select() {
        super.select();
        eventEmitter.dispatch("mindMapToolMenu.visibility", { isVisible: true });
        eventEmitter.dispatch("mindMapToolMenu.activePreset", {
            preset: MIDNMAP_LIST_DATA[0].code,
        });
    }

    unselect() {
        super.unselect();
        eventEmitter.dispatch("mindMapToolMenu.visibility", { isVisible: false });
        this.ghost = null;
    }

    getWidgetWrapper(isGhost = false) {
        const pt = canv.actionManager.getActionPoint();

        const creationStartX = pt.x;
        const creationStartY = pt.y;

        const newElement = new MindMapWidget(creationStartX, creationStartY);

        const newGroup = new Group(true, isGhost);
        newElement.setParent(newGroup);

        return newGroup;
    }

    initRoot(mindMap) {
        const root = mindMap.getRootNode();
        root.textPart.setText(t("mindMap.rootText"));
        root.updateDims();
        return root;
    }

    addIdeas(mindMap, rootNode, ideas: string[], directions: Direction[]) {
        ideas.forEach((text) => {
            directions.forEach((direction) => {
                const node = mindMap.addChild(rootNode, direction, false);
                if (!node) return;

                node.textPart.setText(text);
                node.updateDims();
            });
        });
    }

    finalize(mindMap) {
        mindMap.alignMindMap();
        mindMap.updateDims();
    }

    enrichModeZero(mindMap) {
        const root = this.initRoot(mindMap);

        this.addIdeas(
            mindMap,
            root,
            [t("mindMap.idea1"), t("mindMap.idea2"), t("mindMap.idea3")],
            ["direct"],
        );
        this.finalize(mindMap);
    }

    enrichModeOne(mindMap) {
        this.enrichModeZero(mindMap);
        mindMap.menuDirectionMode("vertical");
    }

    enrichModeTwo(mindMap) {
        const root = this.initRoot(mindMap);

        this.addIdeas(
            mindMap,
            root,
            [t("mindMap.idea1"), t("mindMap.idea2"), t("mindMap.idea3")],
            ["direct", "reverse"],
        );

        this.finalize(mindMap);
    }

    enrichModeThree(mindMap) {
        const root = this.initRoot(mindMap);

        this.addIdeas(
            mindMap,
            root,
            [t("mindMap.idea1"), t("mindMap.idea2")],
            ["direct", "reverse"],
        );

        this.finalize(mindMap);
    }

    setPreset(preset) {
        if (this.ghost == null) {
            this.ghost = this.getWidgetWrapper(true);
        }

        if (preset === "0") {
            this.ghost = this.getWidgetWrapper(true);
            this.enrichModeZero(this.ghost.onlyChild());
        } else if (preset === "1") {
            this.ghost = this.getWidgetWrapper(true);
            this.enrichModeOne(this.ghost.onlyChild());
        } else if (preset === "2") {
            this.ghost = this.getWidgetWrapper(true);
            this.enrichModeTwo(this.ghost.onlyChild());
        } else if (preset === "3") {
            this.ghost = this.getWidgetWrapper(true);
            this.enrichModeThree(this.ghost.onlyChild());
        }

        canv.redraw();
    }

    TryAction(type) {
        // если сразу сделать mouseup не дождавшись mousemove, ghost тоже должен быть
        if (this.ghost === null) {
            this.setPreset("0");
        }
        const ghost = this.ghost;
        if (!ghost) return;

        if (type === "mousedown") {
        } else if (type === "mousemove") {
            const pt = canv.actionManager.getActionPoint();

            const child = ghost.onlyChild() as MindMapWidget;
            if (!child) return;

            const root = child.getRootNode();

            const translateX = pt.x - (root?.startX || 0);
            const translateY = pt.y - (root?.startY || 0);

            ghost.translateGroup(translateX, translateY);
        } else if (type === "mouseup") {
            canv.canvasController.addGroup(ghost);

            canv.eventManager.openRecord();
            canv.eventManager.createBuildEvent("create", ghost);
            canv.eventManager.closeRecord();

            canv.toolBox.TryGetToolByName("cursorTool").setSelectedAndInteraction(ghost);
        }

        canv.redraw();
    }

    draw() {
        if (this.ghost === null) return;

        const memOpacity = canv.ctx.globalAlpha;
        canv.ctx.globalAlpha = 0.5;
        this.ghost.draw();
        canv.ctx.globalAlpha = memOpacity;
    }

    name = "mindMapTool";
    ghost: Group | null = null;
}
